/**
 * The two reasons mail is not going out, surfaced everywhere.
 *
 * When sending is paused, every subsequent send is refused — so this cannot be
 * a detail on one screen. Someone investigating "why did nothing go out" has to
 * meet the answer wherever they happen to be looking.
 *
 * NOT SET UP IS THE SECOND REASON, and it was missing entirely. `paused` means
 * sending worked and was stopped; `ready: false` means it was never configured,
 * which is the state every new organization is in and which resuming does not
 * fix. The attention strip covers it and gates that item on `domains:manage` —
 * which marketing and support do not hold — so for exactly the roles a hosted
 * client is given, the product said nothing at all while every campaign
 * reported success and produced no mail. `ready` is behind `domains:read`,
 * which every preset holds.
 *
 * Both states here rather than a second component: they are mutually exclusive
 * in practice and answer the same question, and two banners that can both
 * appear is how a page ends up with a stack of them.
 */

import { useState } from "react";
import { Link } from "react-router-dom";

import { api, ApiError } from "../api/client";
import { useAsync } from "../hooks";
import { useSession } from "../session";
import type { SendingStatus } from "../api/types";

export function SendingBanner() {
  const { orgId, can } = useSession();
  const { data, reload } = useAsync<SendingStatus>(
    (signal) => api.get<SendingStatus>("/v1/sending-status", { signal }),
    [orgId],
  );

  const [resuming, setResuming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Absent means ready. A deployment older than the field omits it, and every
  // one of those that could send already was — reading absent as not-ready
  // would greet every existing customer with a setup notice on upgrade.
  if (data && data.ready === false && !data.paused) return <NotConfigured canFix={can("domains:manage")} />;

  if (!data?.paused) return null;

  const resume = async () => {
    setResuming(true);
    setError(null);
    try {
      await api.post("/v1/sending-status/resume");
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not resume sending.");
    } finally {
      setResuming(false);
    }
  };

  return (
    <div
      className="oc-panel oc-panel--critical"
      role="alert"
      style={{
        margin: "var(--oc-container-margin) var(--oc-container-margin) 0",
        flex: "none",
      }}
    >
      <div className="oc-panel__body row row--between" style={{ gap: "var(--oc-space-4)" }}>
        <div className="grow">
          <div className="oc-body-sm" style={{ fontWeight: 600 }}>
            Sending is paused
          </div>
          <div className="oc-body-sm oc-text-secondary">
            {data.reason ?? "A delivery rate threshold was crossed."}
            {data.paused_at && ` · since ${new Date(data.paused_at).toLocaleString()}`}
          </div>
          {error && <div className="field-error">{error}</div>}
        </div>

        {can("sending:resume") ? (
          <button type="button" className="oc-btn oc-btn--sm" disabled={resuming} onClick={() => void resume()}>
            {resuming ? "Resuming…" : "Resume sending"}
          </button>
        ) : (
          // Deliberately not a disabled button: the reason it cannot be clicked
          // is the useful part, and a greyed control does not explain itself.
          <span className="oc-body-sm oc-text-muted" style={{ whiteSpace: "nowrap" }}>
            Ask an admin or developer to resume
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Sending has never been set up.
 *
 * Two audiences, one fact. An operator can fix it, so they get the screen that
 * does. A client cannot — publishing DNS is the host's job — so they get no
 * link and no records, because handing somebody a task they have no way to
 * complete produces an email to you rather than a verified domain.
 *
 * A plain panel, not `--attention` or `--critical`. Nothing is wrong: this is a
 * new account, and painting that in warning amber is the mistake the attention
 * strip already made once, when a `setup` row rendered under a danger triangle
 * and was the one row in the list that was not a problem.
 */
function NotConfigured({ canFix }: { canFix: boolean }) {
  return (
    <div
      className="oc-panel"
      style={{ margin: "var(--oc-container-margin) var(--oc-container-margin) 0", flex: "none" }}
    >
      <div className="oc-panel__body row row--between" style={{ gap: "var(--oc-space-4)" }}>
        <div className="grow">
          <div className="oc-body-sm" style={{ fontWeight: 600 }}>
            {canFix ? "Sending is not set up yet" : "We are finishing your account setup"}
          </div>
          <div className="oc-body-sm oc-text-secondary">
            {canFix
              ? "No sending domain is verified, so nothing can go out. Campaigns can be written and scheduled in the meantime."
              : "Your sending domain is being verified. You can build your audience and write templates now — nothing will go out until it is live."}
          </div>
        </div>

        {canFix && (
          <Link className="oc-btn oc-btn--sm" to="/domains" style={{ whiteSpace: "nowrap" }}>
            Set up sending
          </Link>
        )}
      </div>
    </div>
  );
}
